import {
  AlertCircle,
  Archive,
  CheckCircle,
  Clock,
  Download,
  Edit,
  MessageSquare,
  MoreHorizontal,
  Plus,
  RotateCw,
  Send,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../contexts/ToastContext';
import drawingService from '../../services/drawingService';
import { DrawingComment, DrawingStatus, ProjectDrawing } from '../../types';

interface DrawingViewerProps {
  drawing: ProjectDrawing | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload?: (drawing: ProjectDrawing) => void;
  onStatusChange?: (drawing: ProjectDrawing, newStatus: DrawingStatus) => void;
  canEdit?: boolean;
  canApprove?: boolean;
}

const DrawingViewer: React.FC<DrawingViewerProps> = ({
  drawing,
  isOpen,
  onClose,
  onDownload,
  onStatusChange,
  canEdit = false,
  canApprove = false,
}) => {
  const toast = useToast();

  // Refs
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showComments, setShowComments] = useState(true);
  const [comments, setComments] = useState<DrawingComment[]>([]);
  const [, setLoadingComments] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentPosition, setNewCommentPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [selectedComment, setSelectedComment] = useState<string | null>(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [newCommentPriority, setNewCommentPriority] = useState<
    'low' | 'medium' | 'high' | 'critical'
  >('medium');
  const [newCommentCategory, setNewCommentCategory] = useState<
    'design' | 'structural' | 'coordination' | 'quality' | 'safety' | 'other'
  >('design');

  // Load real comments from API
  const loadComments = async () => {
    if (!drawing) return;

    setLoadingComments(true);
    try {
      const result = await drawingService.getDrawingComments(drawing._id, drawing.project_id);
      if (result.success) {
        setComments(result.data || []);
      } else {
        toast.error('Failed to load comments');
      }
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    if (drawing && isOpen) {
      loadComments();
    }
  }, [drawing, isOpen]);

  // Reset state when drawing changes
  useEffect(() => {
    if (drawing) {
      setZoom(1);
      setRotation(0);
      setPan({ x: 0, y: 0 });
      setSelectedComment(null);
      setIsAddingComment(false);
      setNewCommentPosition(null);
    }
  }, [drawing]);

  // Handle mouse events for panning
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isAddingComment) return;

    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle click for adding comments
  const handleImageClick = (e: React.MouseEvent) => {
    if (!isAddingComment || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    setNewCommentPosition({ x, y });
  };

  // Zoom functions
  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 5));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 0.1));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  // Reset view
  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  // Comment functions
  const addComment = async () => {
    if (!newCommentText.trim() || !newCommentPosition || !drawing) return;

    try {
      const result = await drawingService.createDrawingComment(
        {
          drawing_id: drawing._id,
          x: newCommentPosition.x,
          y: newCommentPosition.y,
          content: newCommentText,
          priority: newCommentPriority,
          category: newCommentCategory,
        },
        drawing.project_id
      );

      if (result.success) {
        // Reload comments to get the latest data
        await loadComments();
        setNewCommentText('');
        setNewCommentPosition(null);
        setIsAddingComment(false);
        toast.success('Comment added successfully');
      } else {
        toast.error(result.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const addReply = async (commentId: string) => {
    if (!replyText.trim() || !drawing) return;

    try {
      const result = await drawingService.replyToDrawingComment(
        {
          comment_id: commentId,
          content: replyText,
        },
        drawing.project_id
      );

      if (result.success) {
        // Reload comments to get the latest data
        await loadComments();
        setReplyText('');
        toast.success('Reply added successfully');
      } else {
        toast.error(result.message || 'Failed to add reply');
      }
    } catch (error) {
      console.error('Failed to add reply:', error);
      toast.error('Failed to add reply');
    }
  };

  const toggleCommentResolved = async (commentId: string) => {
    if (!drawing) return;

    const comment = comments.find(c => c.id === commentId);
    if (!comment) return;

    try {
      const result = await drawingService.toggleCommentResolution(
        commentId,
        !comment.resolved,
        drawing.project_id
      );

      if (result.success) {
        // Update local state immediately for better UX
        setComments(prev =>
          prev.map(c => (c.id === commentId ? { ...c, resolved: !c.resolved } : c))
        );
        toast.success(`Comment ${!comment.resolved ? 'resolved' : 'reopened'} successfully`);
      } else {
        toast.error(result.message || 'Failed to update comment');
      }
    } catch (error) {
      console.error('Failed to toggle comment resolution:', error);
      toast.error('Failed to update comment');
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?') || !drawing) return;

    try {
      const result = await drawingService.deleteDrawingComment(commentId, drawing.project_id);

      if (result.success) {
        // Remove from local state
        setComments(prev => prev.filter(comment => comment.id !== commentId));
        setSelectedComment(null);
        toast.success('Comment deleted successfully');
      } else {
        toast.error(result.message || 'Failed to delete comment');
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  // Status change
  const handleStatusChange = async (newStatus: DrawingStatus) => {
    if (drawing && onStatusChange) {
      try {
        // Update status via API
        await drawingService.updateDrawingMetadata(
          drawing._id,
          { status: newStatus },
          drawing.project_id
        );
        onStatusChange(drawing, newStatus);
        toast.success(`Drawing status updated to ${newStatus.replace('_', ' ')}`);
      } catch (error) {
        console.error('Failed to update drawing status:', error);
        toast.error('Failed to update drawing status');
      }
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  // Get status info
  const getStatusInfo = (status: DrawingStatus) => {
    switch (status) {
      case 'draft':
        return { icon: Edit, color: 'text-gray-600', label: 'Draft' };
      case 'under_review':
        return { icon: Clock, color: 'text-yellow-600', label: 'Under Review' };
      case 'approved':
        return { icon: CheckCircle, color: 'text-green-600', label: 'Approved' };
      case 'superseded':
        return { icon: AlertCircle, color: 'text-orange-600', label: 'Superseded' };
      case 'archived':
        return { icon: Archive, color: 'text-red-600', label: 'Archived' };
      default:
        return { icon: Edit, color: 'text-gray-600', label: status };
    }
  };

  if (!isOpen || !drawing) return null;

  const statusInfo = getStatusInfo(drawing.status);
  const StatusIcon = statusInfo.icon;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-90 z-[100] flex">
      {/* Main Viewer */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>

            <div>
              <h2 className="text-lg font-semibold text-gray-900">{drawing.name}</h2>
              <div className="flex items-center space-x-3 text-sm text-gray-500">
                <span>v{drawing.metadata.version}</span>
                {drawing.metadata.revision && <span>Rev. {drawing.metadata.revision}</span>}
                {drawing.metadata.drawing_number && <span>#{drawing.metadata.drawing_number}</span>}
                <span className={`inline-flex items-center ${statusInfo.color}`}>
                  <StatusIcon className="h-4 w-4 mr-1" />
                  {statusInfo.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Status Change Dropdown */}
            {canEdit && (
              <select
                value=""
                onChange={e => {
                  if (e.target.value) {
                    handleStatusChange(e.target.value as DrawingStatus);
                    e.target.value = '';
                  }
                }}
                className="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white"
              >
                <option value="" disabled>
                  Change Status
                </option>
                {drawing.status !== 'draft' && <option value="draft">Draft</option>}
                {drawing.status !== 'under_review' && (
                  <option value="under_review">Under Review</option>
                )}
                {canApprove && drawing.status !== 'approved' && (
                  <option value="approved">Approved</option>
                )}
                {drawing.status !== 'superseded' && <option value="superseded">Superseded</option>}
                {drawing.status !== 'archived' && <option value="archived">Archived</option>}
              </select>
            )}

            {/* Comments Toggle */}
            <button
              onClick={() => setShowComments(!showComments)}
              className={`flex items-center px-3 py-2 rounded-lg transition-colors ${
                showComments
                  ? 'bg-brand-50 text-brand-primary'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Comments ({comments.length})
            </button>

            {/* Download */}
            {onDownload && (
              <button
                onClick={() => onDownload(drawing)}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-gray-50 border-b border-gray-200 p-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleZoomOut}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ZoomOut className="h-4 w-4" />
            </button>

            <span className="text-sm text-gray-600 min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </span>

            <button
              onClick={handleZoomIn}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ZoomIn className="h-4 w-4" />
            </button>

            <div className="w-px h-6 bg-gray-300 mx-2" />

            <button
              onClick={handleRotate}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RotateCw className="h-4 w-4" />
            </button>

            <button
              onClick={resetView}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Reset View
            </button>

            <div className="w-px h-6 bg-gray-300 mx-2" />

            <button
              onClick={() => setIsAddingComment(!isAddingComment)}
              className={`flex items-center px-3 py-1.5 text-sm rounded-lg transition-colors ${
                isAddingComment
                  ? 'bg-red-500 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Comment
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {drawing.metadata.discipline} • {drawing.metadata.phase}
            {drawing.location?.site && ` • ${drawing.location.site}`}
            {drawing.location?.building && ` - ${drawing.location.building}`}
          </div>
        </div>

        {/* Drawing Display */}
        <div
          ref={containerRef}
          className="flex-1 overflow-hidden bg-gray-100 relative cursor-move"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {drawing.file_info.mime_type.startsWith('image/') ? (
            <div className="w-full h-full flex items-center justify-center">
              <img
                ref={imageRef}
                src={drawing.file_url}
                alt={drawing.name}
                className="max-w-none"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg) translate(${pan.x}px, ${pan.y}px)`,
                  cursor: isAddingComment ? 'crosshair' : isDragging ? 'grabbing' : 'grab',
                }}
                onClick={handleImageClick}
                draggable={false}
              />

              {/* Comment Pins */}
              {showComments &&
                comments.map(comment => (
                  <div
                    key={comment.id}
                    className="absolute"
                    style={{
                      left: `${comment.x * 100}%`,
                      top: `${comment.y * 100}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <button
                      onClick={() =>
                        setSelectedComment(selectedComment === comment.id ? null : comment.id)
                      }
                      className={`w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-xs font-medium ${
                        comment.resolved
                          ? 'bg-green-500 text-white'
                          : selectedComment === comment.id
                            ? 'bg-red-500 text-white'
                            : 'bg-red-500 text-white'
                      }`}
                    >
                      {comments.indexOf(comment) + 1}
                    </button>
                  </div>
                ))}

              {/* New Comment Position */}
              {newCommentPosition && (
                <div
                  className="absolute"
                  style={{
                    left: `${newCommentPosition.x * 100}%`,
                    top: `${newCommentPosition.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <div className="w-6 h-6 rounded-full bg-red-500 border-2 border-white shadow-lg animate-pulse" />
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">📄</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {drawing.file_info.file_extension.toUpperCase()} File
                </h3>
                <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                {onDownload && (
                  <button
                    onClick={() => onDownload(drawing)}
                    className="inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download to View
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comments Sidebar */}
      {showComments && (
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
          {/* Comments Header */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Comments ({comments.length})</h3>
            <p className="text-sm text-gray-500 mt-1">Click on the drawing to add a comment</p>
          </div>

          {/* Comments List */}
          <div className="flex-1 overflow-y-auto">
            {comments.length === 0 ? (
              <div className="p-6 text-center">
                <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-sm font-medium text-gray-900 mb-2">No comments yet</h4>
                <p className="text-sm text-gray-500">
                  Click "Add Comment" and then click on the drawing to start a discussion.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {comments.map((comment, index) => (
                  <div
                    key={comment.id}
                    className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                      selectedComment === comment.id ? 'bg-red-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() =>
                      setSelectedComment(selectedComment === comment.id ? null : comment.id)
                    }
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          comment.resolved ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                        }`}
                      >
                        {index + 1}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {comment.author.name}
                            </span>

                            {/* Priority Badge */}
                            {comment.priority && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  comment.priority === 'critical'
                                    ? 'bg-red-100 text-red-800'
                                    : comment.priority === 'high'
                                      ? 'bg-orange-100 text-orange-800'
                                      : comment.priority === 'medium'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {comment.priority.toUpperCase()}
                              </span>
                            )}

                            {/* Category Badge */}
                            {comment.category && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                                {comment.category}
                              </span>
                            )}

                            {comment.resolved && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Resolved
                              </span>
                            )}
                          </div>

                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-500">
                              {formatDate(comment.created_at)}
                            </span>

                            <button className="p-1 text-gray-400 hover:text-gray-600">
                              <MoreHorizontal className="h-3 w-3" />
                            </button>
                          </div>
                        </div>

                        <p className="text-sm text-gray-700 mt-1">{comment.content}</p>

                        {/* Replies */}
                        {comment.replies.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {comment.replies.map(reply => (
                              <div key={reply.id} className="pl-4 border-l-2 border-gray-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-gray-700">
                                    {reply.author.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(reply.created_at)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">{reply.content}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reply Input */}
                        {selectedComment === comment.id && (
                          <div className="mt-3 space-y-2">
                            <textarea
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                              placeholder="Add a reply..."
                              className="w-full p-2 text-sm border border-gray-300 rounded-lg resize-none"
                              rows={2}
                            />
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => toggleCommentResolved(comment.id)}
                                  className={`text-xs px-2 py-1 rounded ${
                                    comment.resolved
                                      ? 'text-red-600 hover:bg-red-50'
                                      : 'text-green-600 hover:bg-green-50'
                                  }`}
                                >
                                  {comment.resolved ? 'Reopen' : 'Resolve'}
                                </button>

                                <button
                                  onClick={() => deleteComment(comment.id)}
                                  className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded"
                                >
                                  Delete
                                </button>
                              </div>

                              <button
                                onClick={() => addReply(comment.id)}
                                disabled={!replyText.trim()}
                                className="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Reply
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* New Comment Input */}
          {newCommentPosition && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Add Comment</h4>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newCommentPriority}
                    onChange={e => setNewCommentPriority(e.target.value as any)}
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newCommentCategory}
                    onChange={e => setNewCommentCategory(e.target.value as any)}
                    className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                  >
                    <option value="design">Design</option>
                    <option value="structural">Structural</option>
                    <option value="coordination">Coordination</option>
                    <option value="quality">Quality</option>
                    <option value="safety">Safety</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <textarea
                value={newCommentText}
                onChange={e => setNewCommentText(e.target.value)}
                placeholder="Describe the issue or suggestion..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-red-500"
                rows={3}
                autoFocus
              />

              <div className="flex items-center justify-end space-x-2 mt-3">
                <button
                  onClick={() => {
                    setNewCommentPosition(null);
                    setNewCommentText('');
                    setIsAddingComment(false);
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={addComment}
                  disabled={!newCommentText.trim()}
                  className="flex items-center px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="h-3 w-3 mr-1" />
                  Add Comment
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>,
    document.body
  );
};

export default DrawingViewer;
